import { useEffect } from "react";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { useMatch, useSearchParams } from "react-router-dom";
import { scheduleRoutesCombined } from "../ScheduleNavigation";
import { PageDefaultSegmentWithHeader, useFeedback } from "@sis/react-library";
import { ScheduleWinActionsLeft } from "./ScheduleWinActionsLeft";
import { Box, useTheme } from "@mui/material";
import { LoadingBackground } from "../../shared/LoadingBackground";
import { ScheduleWinRunners } from "./ScheduleWinRunners";
import { getSearchCategory, getSearchDate, updateSearchParamsForCategory, updateSearchParamsForDate } from "../schedulePage/schedulePageHelper";
import { rdsMeeting_Dto } from "../../../interfaces/dtos/rdsMeeting";
import { LoadingError } from "../../shared/LoadingError";
import { loadEventWin, updateBettingState } from "../../../redux/win/winActions";
import { winState } from "../../../redux/win/winSlice";
import { rdsState } from "../../../redux/rds/rdsSlice";
import { getDateKey } from "../../../redux/common/commonActions";
import { ScheduleCommonDetails } from "../scheduleCommon/ScheduleCommonDetails";
import { ScheduleWinMarketDeductions } from "./ScheduleWinMarketDeductions";
import { ScheduleWinActionsRight } from "./ScheduleWinActionsRight";
import { dispatchIsSuccessful, getEventDescForTitle, segmentHeaderDefault, toTitleCase } from "../../../utils/commonUtils";
import { trackState } from "../../../redux/track/trackSlice";
import { createTrackCodeKey, loadTrack } from "../../../redux/track/trackAction";
import { RppTitle } from "../../shared/RppTitle";

export const ScheduleWinPage = () => {

  const match = useMatch(scheduleRoutesCombined.win_eventMatch);
  const eventIdAsString = match?.params.eventId ?? "0";
  const eventId = Number.parseInt(eventIdAsString);

  const win = useAppSelector(winState);
  const rds = useAppSelector(rdsState);
  const { tracks } = useAppSelector(trackState);
  const dispatch = useAppDispatch();
  const { showSuccess, showError } = useFeedback();
  const theme = useTheme();

  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    dispatch(loadEventWin(eventId))
    // we only want the conditions below
    // eslint-disable-next-line
  }, [match])

  const event = rds.rdsEvents.find((e) => e.id === eventId);
  const eventWins = win.eventWins.filter((e) => e.eventId === eventId);
  const currentEventWin = eventWins.length > 0 ?
    eventWins.toSorted((a, b) => ((a.version > b.version ? -1 : 1)))[0] :
    undefined;

  const meeting = rds.rdsMeetings.find((m) => m.id === event?.meetingId);
  const rdsTrack = rds.rdsTracks.find((t) => t.id === meeting?.trackId);

  const getTrackCodeKey = () => {
    return rdsTrack && currentEventWin ?
      createTrackCodeKey(rdsTrack.sportCode, currentEventWin.categoryCode ?? '', currentEventWin.country ?? '') :
      '';
  }

  const track = tracks.find(t => t.provider === currentEventWin?.provider && t.trackCodeKey === getTrackCodeKey());

  useEffect(() => {
    if (currentEventWin && rdsTrack) {
      const nullFields = [];
      if (!currentEventWin.provider) { nullFields.push('provider'); }
      if (!currentEventWin.categoryCode) { nullFields.push('categoryCode'); }
      if (!currentEventWin.country) { nullFields.push('country'); }
      if (nullFields.length === 0) {
        dispatch(loadTrack({ provider: currentEventWin.provider!, trackCodeKey: getTrackCodeKey() }))
      } else {
        showError(<>Could not load track:<br />The following values are not present on the win market:<br />{nullFields.map(n => `${n}, `)}</>);
      }
    }
    // we only want the conditions below
    // eslint-disable-next-line
  }, [currentEventWin, rdsTrack])

  const checkSearchParams = (meeting: rdsMeeting_Dto) => {
    const searchDate = getSearchDate(searchParams);
    const searchCategory = getSearchCategory(searchParams);
    if (!searchDate || getDateKey(searchDate) !== getDateKey(meeting.startDate)) {
      setSearchParams(updateSearchParamsForDate(searchParams, meeting.startDate));
    }
    if (!searchCategory || searchCategory !== meeting.categoryCode) {
      setSearchParams(updateSearchParamsForCategory(searchParams, meeting.categoryCode));
    }
  }

  useEffect(() => {
    if (event) {
      const meeting = rds.rdsMeetings.find((m) => m.id === event.meetingId);
      if (meeting) { checkSearchParams(meeting); }
    }
    // we only want the conditions below
    // eslint-disable-next-line
  }, [rds])

  const getBettingPhase = () => {
    return currentEventWin?.bettingState ?
      `${toTitleCase(currentEventWin?.bettingState)} Prices` : '';
  }

  const callUpdateBettingState = async (newBettingPhase: string) => {
    if (currentEventWin) {
      const response = await dispatch(updateBettingState(
        {
          eventId: currentEventWin.eventId, bettingState: newBettingPhase,
          currentVersion: currentEventWin.version
        })
      )
      if (dispatchIsSuccessful(response)) {
        showSuccess(`Betting state successfully set to ${newBettingPhase} prices.`);
      }
    }
  }

  return (
    <PageDefaultSegmentWithHeader
      id='schedule-win'
      width='100%'
      headingElement={<ScheduleWinActionsLeft currentEventWin={currentEventWin} event={event} track={track} callUpdateBettingState={callUpdateBettingState} />}
      headingRightElement={<ScheduleWinActionsRight eventId={eventId} event={event} currentEventWin={currentEventWin} track={track} />}
      headingBackground={segmentHeaderDefault(theme)}
      headingColor={theme.palette.common.white}
    >
      <RppTitle title={`Win:  ${getEventDescForTitle(meeting, event)}`} />
      {win.loading &&
        <Box sx={{ height: '150px' }}><LoadingBackground /></Box>
      }
      {!win.loading && !event &&
        <LoadingError sx={{ marginTop: '10px' }}> Event not found {eventId}</LoadingError>
      }
      {!win.loading && event &&
        <>
          <ScheduleCommonDetails
            event={event}
            bettingPhase={getBettingPhase()}
            suspended={currentEventWin?.calculated?.isSuspended ?? false}
            meeting={meeting}
          />
          <ScheduleWinRunners eventId={eventId} currentEventWin={currentEventWin} />
          {currentEventWin?.sampleMarketDeductions && currentEventWin.sampleMarketDeductions.length > 0 &&
            <ScheduleWinMarketDeductions eventWin={currentEventWin} />}
        </>
      }
    </PageDefaultSegmentWithHeader>
  )
}